import React, { useCallback, useRef, useState } from 'react';
import { Button, Flex, Input, InputRef } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { ColumnType } from 'antd/es/table';

import { initialFilters } from '../../attributes/utils/options';
import { useTheme } from '../../../../../hooks/useTheme';

export function useFilters<Filters, Data>(lazyQueryHook, incomeInitialFilters) {
    const [
        get,
        {
            data: source = { items: [], total: 0 },
            isLoading,
            isFetching,
            isSuccess,
        },
    ] = lazyQueryHook();

    const { colorPrimary } = useTheme();

    const [filters, setFilters] = useState<Filters>(
        incomeInitialFilters
            ? incomeInitialFilters
            : (initialFilters as Filters)
    );
    const searchInput = useRef<InputRef>(null);

    const updateFilters = (update: Partial<Filters>) => {
        setFilters(prev => ({
            ...prev,
            ...update,
        }));
    };

    const fetchCallback = useCallback(get, [get]);

    const onSearch = (value: string, dataIndex: string) => {
        const payload = {
            [dataIndex]: value,
        } as Partial<Filters>;

        return updateFilters(payload);
    };

    const onConfirm = () => {
        updateFilters(filters);
    };

    const resetFilters = dataIndex => {
        setFilters(prevValues => {
            const newValues = { ...prevValues };
            delete newValues[dataIndex];
            return newValues;
        });
    };

    function getColumnSearchProps(
        dataIndex: keyof Filters,
        fieldTitle: string
    ): ColumnType<Data> {
        return {
            filterDropdown: ({ close }) => (
                <div
                    style={{ padding: 8 }}
                    onKeyDown={e => e.stopPropagation()}
                >
                    <Input
                        ref={searchInput}
                        placeholder={`Поиск по ${fieldTitle}`}
                        value={filters[dataIndex] as string}
                        onChange={e =>
                            onSearch(e.target.value, dataIndex as string)
                        }
                        onPressEnter={onConfirm}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Flex gap={4} justify='space-between'>
                        <Button
                            type='primary'
                            onClick={() => {
                                onConfirm();
                                close();
                            }}
                            icon={<SearchOutlined />}
                            size='small'
                            style={{ width: 90 }}
                        >
                            Поиск
                        </Button>
                        <Button
                            onClick={() => {
                                resetFilters(dataIndex as string);
                                close();
                            }}
                            size='small'
                            style={{ width: 90 }}
                        >
                            Очистить
                        </Button>
                        <Button
                            type='link'
                            size='small'
                            onClick={() => {
                                close();
                            }}
                        >
                            Отмена
                        </Button>
                    </Flex>
                </div>
            ),
            filteredValue: filters[dataIndex]
                ? [String(filters[dataIndex])]
                : null,
            filterIcon: (filtered: boolean) => (
                <SearchOutlined
                    style={{
                        color: filtered ? colorPrimary : undefined,
                    }}
                />
            ),
        };
    }
    const loading = isLoading || isFetching;

    return {
        updateFilters,
        filters,
        get: fetchCallback,
        source,
        loading,
        isSuccess,
        getColumnSearchProps,
    };
}
